'use client'

import { Text } from '@components/Helpers'
import {
  Box,
  Button,
  Table,
  TableCell,
  TableRow,
  styled,
  TableBody,
  Fade,
} from '@mui/material'
import Image from 'next/image'
import { ArrowLeft } from 'lucide-react'
import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ErrorMessage from '@components/ErrorMessage'
import ExplorerButtons from '@components/ExplorerButtons'
import { ExplorerContext } from '@context/Explorer/context'

const DetailsGroup = styled(Box)(({ theme }) => ({
  marginTop: 30,
  [theme.breakpoints.down('lg')]: {
    display: 'block',
  },
  [theme.breakpoints.up('lg')]: {
    display: 'flex',
    alignItems: 'center',
  },
}))

const NFTImage = styled(Image)(({ theme }) => ({
  borderRadius: 'var(--dewi_radius_lg)',
  imageRendering: 'pixelated',
  [theme.breakpoints.down('md')]: {
    width: '50%',
    height: '50%',
  },
}))

const ImageGroup = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('lg')]: {
    display: 'flex',
    justifyContent: 'center',
  },
}))

const TableGroup = styled(Box)(({ theme }) => ({
  height: 270,
  [theme.breakpoints.up('lg')]: {
    width: '70%',
    marginLeft: 20,
  },
  [theme.breakpoints.down('lg')]: {
    width: '100%',
    marginTop: 30,
  },
}))

export type NFTDetailsCat = {
  n: number
  name: string
  mint: string
  hasImage: boolean
  attributes: { trait_type: string; value: string }[]
}

type NFTDetailsProps = {
  cat?: NFTDetailsCat
}

/**
 * The single-cat view inside the explorer, opened with ?dewi=<n>.
 *
 * Everything on it comes from the static manifest handed down by the server
 * page, so opening a cat is a navigation, not a fetch.
 */
function NFTDetails({ cat }: NFTDetailsProps) {
  const { t } = useTranslation()
  const { exitNFTHandler } = useContext(ExplorerContext)
  // The artwork is self hosted, so there is nothing to wait for and the panel
  // is already visible in the server-rendered HTML.
  const [imageLoaded, setImageLoaded] = useState(true)

  if (!cat) {
    return (
      <section>
        <Button variant="contained" onClick={exitNFTHandler}>
          <ArrowLeft size={18} strokeWidth={1.75} />
          {t('explorerPage.overview.nftDetails.exit')}
        </Button>
        <div className="flex justify-center items-center w-screen">
          <ErrorMessage text={t('explorerPage.overview.nftDetails.error')} />
        </div>
      </section>
    )
  }

  const traits = cat.attributes.filter(
    ({ value }) => value && value.toLowerCase() !== 'none',
  )

  return (
    <Box component="section">
      <Button variant="contained" onClick={exitNFTHandler}>
        <ArrowLeft size={18} strokeWidth={1.75} />
        {t('explorerPage.overview.nftDetails.exit')}
      </Button>
      <DetailsGroup>
        <ImageGroup>
          {cat.hasImage ? (
            <Fade appear={false} in={imageLoaded} timeout={1000}>
              <NFTImage
                width={300}
                height={300}
                // The detail view is one cat, so the animation is the point.
                src={`/cats/${cat.n}.gif`}
                alt={cat.name}
                onLoad={() => setImageLoaded(true)}
              />
            </Fade>
          ) : (
            <Box
              width={300}
              height={300}
              borderRadius="var(--dewi_radius_lg)"
              display="flex"
              alignItems="center"
              justifyContent="center"
              className="bg-dark-7 text-gray-4"
            >
              <span className="text-4xl font-bold">#{cat.n}</span>
            </Box>
          )}
        </ImageGroup>
        <TableGroup>
          <div className="flex">
            <Text
              sx={{
                fontSize: '2rem',
                flex: 1,
                fontFamily: 'var(--font-display), var(--font-nunito), sans-serif',
                textTransform: 'uppercase',
              }}
            >
              {t('explorerPage.overview.nftDetails.title', {
                number: cat.name,
              })}
            </Text>
            <ExplorerButtons signature={cat.mint} showSignatureText={false} />
          </div>
          <Table>
            <TableBody>
              {traits.map(({ trait_type, value }) => (
                <TableRow key={trait_type}>
                  <TableCell>
                    <Text sx={{ color: 'var(--dewi_colors_gray_6)' }}>
                      {trait_type}
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text>{value}</Text>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableGroup>
      </DetailsGroup>
    </Box>
  )
}

export default NFTDetails
